import { ArrowLeft, FileText, IdCard, FileImage, Shirt } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/api";
import "./SessionsPage.css";
import LoadingSpinner from "./LoadingSpinner";
import { SessionalHoverCards } from "./SessionHoverCard";
import Stack from './Stack'


const SessionsPage = () => {
  const { locationSlug } = useParams();
  const navigate = useNavigate();
  
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const cardRefs = useRef([]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const data = await api.getSessionInfo(locationSlug);
        if (mounted) setSessionInfo(data);
      } catch (err) {
        console.error("Failed to load session info:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [locationSlug]);

  // Intersection Observer for scroll-triggered animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardRefs.current.indexOf(entry.target);
            if (index !== -1) {
              setVisibleCards((prev) => new Set([...prev, index]));
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      cardRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [sessionInfo]);

  if (loading || !sessionInfo) {
    return <LoadingSpinner />;
  }

  // Prepare cards data for SessionHoverCards
  const sessionCards = [
    {
      title: sessionInfo.what_is_title,
      subtitle: "Découvrez",
      description: sessionInfo.what_is_text,
      imageSrc: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80",
      imageAlt: "Kinésithérapie"
    },
    {
      title: sessionInfo.when_consult_title,
      subtitle: "Consultez",
      description: sessionInfo.when_consult_text,
      imageSrc: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800&q=80",
      imageAlt: "Consultation"
    },
    {
      title: sessionInfo.session_process_title,
      subtitle: "Déroulement",
      description: sessionInfo.session_process_text,
      imageSrc: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80",
      imageAlt: "Séance"
    }
  ].filter(card => card.description); // Only include cards with content

  const documentsCards = [
    { 
      id: 1, 
      icon: <FileText size={80} strokeWidth={1.5} />,
      label: "Prescription médicale"
    },
    { 
      id: 2, 
      icon: <IdCard size={80} strokeWidth={1.5} />,
      label: "Carte Vitale"
    },
    { 
      id: 3, 
      icon: <FileImage size={80} strokeWidth={1.5} />,
      label: "Imagerie médicale"
    },
    { 
      id: 4, 
      icon: <Shirt size={80} strokeWidth={1.5} />,
      label: "Tenue de sport"
    }
  ];

  return (
    <div className="sessions-page">
      {/* Navigation Header */}
      <div className="page-header">
        <div className="container">
          <button
            className="back-btn"
            onClick={() => navigate(`/${locationSlug}`)}
          >
            <ArrowLeft size={20} /> Retour
          </button>
          <h1 className="heading-lg">Informations Pratiques</h1>
          <p className="body-lg" style={{ color: "var(--text-secondary)", marginTop: "1rem", textAlign: "center" }}>
            Tout ce que vous devez savoir sur vos séances de kinésithérapie
          </p>
        </div>
      </div>

      {/* Section 1: What is, When to consult, Session process */}
      <section className="section section-primary">
        <div className="container">
          <SessionalHoverCards cards={sessionCards} />
        </div>
      </section>

      {/* Section 2: Required documents */}
      <section className="section section-secondary">
        <div className="container">
          <div className="card-swap-section">
            <div className="card-swap-content">
              <div className="card-swap-text">
                <h2 className="heading-lg">{sessionInfo.required_docs_title}</h2>
                <p className="body-lg" style={{ color: "var(--text-secondary)", marginTop: "1.5rem", lineHeight: "1.8", textAlign: "justify" }}>
                  {sessionInfo.required_docs_text}
                </p>
              </div>
              <div style={{ position: 'relative', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Stack
                  randomRotation={true}
                  sensitivity={180}
                  sendToBackOnClick={false}
                  cardDimensions={{ width: 250, height: 300 }}
                  cardsData={documentsCards}
                  autoSwap={true}
                  autoSwapInterval={3000}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Refund */}
      <section className="section section-primary">
        <div className="container">
          <div className="refund-section">
            {/* Refund section */}
            {sessionInfo.refund_text && (
              <div
                ref={(el) => (cardRefs.current[4] = el)}
                className={`refund-content ${visibleCards.has(4) ? "visible" : ""}`}
              >
                <h2 className="heading-lg">{sessionInfo.refund_title}</h2>
                <p className="body-md" style={{ whiteSpace: "pre-wrap", textAlign: "justify" }}>
                  {sessionInfo.refund_text}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
    
  );
};

export default SessionsPage;